import React from 'react';
import { Tag, Progress } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTheme } from '../../../context/ThemeContext';
import Table from '../../../commonComponents/Table';
import {
  RankCell,
  ItemCell,
  TimesSoldCell,
  RevenueCell,
  PopularityCell,
  GrowthCell,
} from './styles';
import { PopularItemsTableProps } from './types';
import { TREND_ICON_CONFIG, TREND_TYPES } from './constants';

const PopularItemsTable: React.FC<PopularItemsTableProps> = ({ items, isLoading }) => {
  const { isDarkMode } = useTheme();
  const themeMode: 'light' | 'dark' = isDarkMode ? 'dark' : 'light';

  const getTrendIcon = (trend: string) => {
    const trendConfig = TREND_ICON_CONFIG[trend] || TREND_ICON_CONFIG[TREND_TYPES.NEUTRAL];
    return <FontAwesomeIcon icon={trendConfig.icon} color={trendConfig.color} />;
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      width: 80,
      render: (_, __, index) => (
        <RankCell>#{index + 1}</RankCell>
      ),
    },
    {
      title: 'Item',
      dataIndex: 'item',
      key: 'item',
      render: (text: string) => <ItemCell>{text}</ItemCell>,
    },
    {
      title: 'Category',
      dataIndex: 'categoryName',
      key: 'categoryName',
      render: (text: string) => (
        <Tag color="blue" style={{ borderRadius: '4px' }}>
          {text}
        </Tag>
      ),
    },
    {
      title: 'Times Sold',
      dataIndex: 'totalTimesSold',
      key: 'totalTimesSold',
      render: (value: number) => (
        <TimesSoldCell>{value.toLocaleString()}</TimesSoldCell>
      ),
    },
    {
      title: 'Revenue',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      render: (value: number) => (
        <RevenueCell>${value.toLocaleString()}</RevenueCell>
      ),
    },
    {
      title: 'Popularity',
      dataIndex: 'popularityScore',
      key: 'popularityScore',
      render: (score: number) => (
        <PopularityCell>
          <span>{score}/100</span>
          <Progress
            percent={score}
            strokeColor={{
              '0%': '#4299E1',
              '100%': '#63B3ED',
            }}
            showInfo={false}
            size="small"
          />
        </PopularityCell>
      ),
    },
    {
      title: 'Growth',
      key: 'growth',
      render: (_, record) => (
        <GrowthCell>
          <span>Vol: {record.volumeGrowth > 0 ? '+' : ''}{record.volumeGrowth}%</span>
          <span>Rev: {record.revenueGrowth > 0 ? '+' : ''}{record.revenueGrowth}%</span>
        </GrowthCell>
      ),
    },
    {
      title: 'Trend',
      dataIndex: 'trend',
      key: 'trend',
      width: 80,
      render: (trend: string) => getTrendIcon(trend),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={items}
      rowKey={(record) => `item-${record.item}-${record.categoryName}`}
      loading={isLoading}
      pagination={false}
      scroll={{ x: true, y: 600 }}
      emptyText="No popular items found"
      emptyDescription="No popular items available for the selected period"
      headerBackgroundColor={isDarkMode ? '#2D3748' : '#F7FAFC'}
      removeVerticalBorders={true}
      thPadding="16px 12px"
      tdPadding="16px 12px"
      firstChildBorderRadius="8px"
      border="0"
      borderBottomLeftRight={true}
      theme={themeMode}
    />
  );
};

export default PopularItemsTable;
